import { useTranslation } from "react-i18next"
import GlobalContext, { GlobalStateSchema } from "../context/GlobalContext"
import { Languages, languageIds } from "../../language"
import { navigate } from "gatsby"
import type { SchemaElement } from "../context/StateSchema"

interface FileWithSize {
  size: number
}

interface ButtonConfig {
  label: string
  value: string
}

export const getTotalSizeFiles = (files: FileWithSize[]): number => {
  return files.reduce(function (total, file) {
    return total + file.size
  }, 0)
}

export const includeFilesInPostPayload = (files: FileWithSize[]): boolean => {
  /* Grense aws lambda er 6MB */
  const maxAwsPayload = 6291456 /* 6MB */
  const maxMessageSize = 32768 /* Skjema med fulle tekstområder var ca. 7KB - 32KB for litt margin på tekst++ */

  const totalSizeFiles = getTotalSizeFiles(files)
  const totalSizeFilesBase64 =
    totalSizeFiles *
    (4 /
      3) /* Api gateway base64 encoder filene før de går til lambda, og øker derfor i størrelse */
  return totalSizeFilesBase64 + maxMessageSize < maxAwsPayload
}

export const debounce = <T extends (...args: any[]) => any>(
  fn: T,
  delay: number
): ((...args: Parameters<T>) => void) => {
  let timer: NodeJS.Timeout | null = null
  return function (this: any, ...args: Parameters<T>) {
    const context = this
    if (timer) clearTimeout(timer)
    timer = setTimeout(function () {
      fn.apply(context, args)
    }, delay)
  }
}

export const isNullOrWhitespace = (
  value: string | null | undefined
): boolean => {
  return !value || !value.trim()
}

export const removeWhitespace = (value: string): string => {
  return value.replace(/\s/g, "")
}

export const isOnlyDigits = (value: string): boolean => {
  return /^\d+$/.test(value)
}

export const hasDigits = (value: string): boolean => {
  return /\d/.test(value)
}

// RFC822
export const isValidEmail = (email: string): boolean => {
  // eslint-disable-next-line
  return /^([^\x00-\x20\x22\x28\x29\x2c\x2e\x3a-\x3c\x3e\x40\x5b-\x5d\x7f-\xff]+|\x22([^\x0d\x22\x5c\x80-\xff]|\x5c[\x00-\x7f])*\x22)(\x2e([^\x00-\x20\x22\x28\x29\x2c\x2e\x3a-\x3c\x3e\x40\x5b-\x5d\x7f-\xff]+|\x22([^\x0d\x22\x5c\x80-\xff]|\x5c[\x00-\x7f])*\x22))*\x40([^\x00-\x20\x22\x28\x29\x2c\x2e\x3a-\x3c\x3e\x40\x5b-\x5d\x7f-\xff]+|\x5b([^\x0d\x5b-\x5d\x80-\xff]|\x5c[\x00-\x7f])*\x5d)(\x2e([^\x00-\x20\x22\x28\x29\x2c\x2e\x3a-\x3c\x3e\x40\x5b-\x5d\x7f-\xff]+|\x5b([^\x0d\x5b-\x5d\x80-\xff]|\x5c[\x00-\x7f])*\x5d))*$/.test(
    email
  )
}

//+#### ######## => 99887766
export const isValidPhoneNumber = (phoneNumber: string): boolean => {
  return (
    (isNullOrWhitespace(phoneNumber) ||
      phoneNumber.length < 8 ||
      !isOnlyDigits(phoneNumber)) === false
  )
}

export const isValidName = (name: string): boolean => {
  return (
    (isNullOrWhitespace(name) || name.length < 2 || hasDigits(name)) === false
  )
}

export const formaterOrganisasjonsnummer = (
  organisasjonsnummer: string
): string => {
  let orgnr = removeWhitespace(organisasjonsnummer)
  if (orgnr.length !== 9) return organisasjonsnummer

  const matched = orgnr.match(/.{1,3}/g)
  return matched ? matched.join(" ") : organisasjonsnummer
}

export const getNestedObject = (object: any, path: string): any => {
  let nested = object
  const pathSegments = !path ? [] : path.split(".")
  for (let i = 0; i < pathSegments.length; ++i) {
    nested = nested[pathSegments[i]]
  }
  return nested
}

export const useTranslationsAsButtonConfig = (
  translationKey: string
): ButtonConfig[] => {
  const { t } = useTranslation()
  const translations = t(translationKey, { returnObjects: true }) as Record<
    string,
    string
  >
  return Object.getOwnPropertyNames(translations).map(prop => {
    return {
      label: translations[prop],
      value: prop,
    }
  })
}

export const useJoinStringArrayTranslation = (
  translationKey: string
): string => {
  const { t } = useTranslation()
  const translations = t(translationKey, { returnObjects: true }) as string[]
  return translations.join("")
}

export const formatDateFromString = (dateString: string): string => {
  // eslint-disable-next-line
  const currentLanguage = GlobalContext.useState(
    GlobalStateSchema.language as any as SchemaElement
  )
  const isoDateTime = dateString.toString() + "Z"
  const parsedDato = new Date(Date.parse(isoDateTime))

  if (currentLanguage == "en") {
    return parsedDato.toLocaleDateString("en-GB")
  } else {
    return parsedDato.toLocaleDateString("no-NO")
  }
}

export const formatTimeFromString = (dateString: string): string => {
  const isoDateTime = dateString.toString() + "Z"
  const parsedDato = new Date(Date.parse(isoDateTime))
  return parsedDato.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  })
}

export const getWeekdayFromDateString = (
  dateString: string,
  weekdayList: string[]
): string => {
  const dayNo = new Date(dateString).getDay()
  return weekdayList[dayNo]
}

// Filendinger støttet i elements: ".xls,.xlt,.xla,.xlc,.xlsx,.xltx,.xlsm,.xltm,.ppt,.pptx,.doc,.dot,.docx,.dotx,.docm,.dotm,.rtf,.wxml,.sxw,.sxc,.sxi,.odt,.ods,.odp,.htm,.html,.txt,.pdf,.awd,.bmi,.bmp,.bw,.cal,.cdr,.cdt,.cel,.clp,.cmx,.cpt,.cur,.cut,.dcx,.dib,.flc,.fli,.ham,.hmr,.hrz,.icn,.ico,.iff,.img,.jff,.jif,.jmx,.lbm,.mac,.mil,.msp,.ofx,.pan,.pat,.pbm,.pc2,.pcd,.pct,.pcx,.pgm,.pic,.png,.pnm,.ppm,.psd,.pyx,.qfx,.ras,.rgb,.rle,.sam,.scx,.sep,.sgi,.st,.stw,.sun,.tga,.udi,.wpg,.zbr,.zmf,.gif,.jpeg,.jpg,.tif,.tiff,.msg,.xps,.oxps,.mpeg2,.mp4,.mov,.wmv,.avi,.wma,.mp3,.wav
// Ny versjon av filvelger/DropZone skal også ha inn mime-type, og listen under inneholder mime-typer funnet for filendingene over.
// Bruker wildcard på mime-type for bilder. Fra elements er det disse filendingene som er støttet:  .awd,.bmp,.cmx,.gif,.jpeg,.jpg,.jif,.ico,.pic,.png,.pbm,.pct,.pcx,.pnm,.ppm,.pgm,.psd,.ras,.rgb,.tif,.tiff
// For alle mime-typer: https://mimetype.io/all-types
export const validFileExtensions: Record<string, string[]> = {
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
    ".docx",
  ],
  "application/pdf": [".pdf"],
  "image/png": [".png"],
  "image/jpeg": [".jpeg", ".jpg", ".jpe", ".pjpg", ".jfif", ".jif"],
  "image/gif": [".gif"],
  "image/bmp": [".bmp"],
  "image/tiff": [".tif", ".tiff"],
  "image/vnd.adobe.photoshop": [".psd"],
  "image/awd": [".awd"],
  "image/x-icon": [".ico"],
  "image/x-pict": [".pct", ".pic"],
  "image/x-cmx": [".cmx"],
  "image/x-pcx": [".pcx"],
  "image/x-portable-bitmap": [".pbm"],
  "image/x-portable-graymap": [".pgm"],
  "image/x-portable-pixmap": [".ppm"],
  "image/x-portable-anymap": [".pnm"],
  "image/x-cmu-raster": [".ras"],
  "image/x-rgb": [".rgb"],
  "application/msword": [".doc", ".dot"],
  "application/vnd.ms-excel": [".xls", ".xlt", ".xla"],
  "application/vnd.ms-powerpoint": [".ppt"],
  "application/vnd.ms-outlook": [".msg"],
  "application/vnd.ms-xpsdocument": [".xps"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.template": [
    ".dotx",
  ],
  "application/vnd.ms-word.document.macroEnabled.12": [".docm"],
  "application/vnd.ms-word.template.macroEnabled.12": [".dotm"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
    ".xlsx",
  ],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.template": [
    ".xlst",
  ],
  "application/vnd.ms-excel.sheet.macroEnabled.12": [".xlsm"],
  "application/vnd.ms-excel.template.macroEnabled.12": [".xltm"],
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": [
    ".pptx",
  ],
  "text/plain": [".txt"],
  "text/html": [".htm", ".html"],
  "application/rtf": [".rtf"],
  "application/vnd.sun.xml.writer": [".sxw"],
  "application/vnd.sun.xml.calc": [".sxc"],
  "application/vnd.sun.xml.impress": [".sxi"],
  "application/vnd.sun.xml.writer.template": [".stw"],
  "application/vnd.oasis.opendocument.text": [".odt"],
  "application/vnd.oasis.opendocument.spreadsheet": [".ods"],
  "application/vnd.oasis.opendocument.presentation": [".odp"],
  "video/x-msvideo": [".avi"],
  "audio/mpeg": [".mp3"],
  "video/mp4": [".mp4"],
  "audio/wav": [".wav"],
  "video/x-ms-wmv": [".wmv"],
  "video/quicktime": [".mov"],
  "application/x-iso9660-image": [".cdr"],
  "application/x-msclip": [".clp"],
  "application/mac-compactpro": [".cpt"],
  "video/x-fli": [".fli"],
}

export const parseJwt = (token: string): any => {
  const base64Url = token.split(".")[1]
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/")
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split("")
      .map(function (c) {
        return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2)
      })
      .join("")
  )

  return JSON.parse(jsonPayload)
}

export const isNotBrowser = (): boolean => typeof window === "undefined"

export const isBrowser = (): boolean => !isNotBrowser()

// Gatsby oppdaterer ikke title-tag i header via head-api når man bytter språk, kun når man går til ny side
// Denne funksjonen kan brukes i disse tilfellene til å sette tittel ut fra overskrift på siden (id = side-overskrift)
export const settTittelFraOverskrift = (): void => {
  if (isBrowser()) {
    const overskrift = document.getElementById("side-overskrift")
    if (overskrift) {
      const tittel = document.getElementById("side-tittel")
      if (tittel) {
        tittel.textContent = overskrift.textContent
      }
    }
  }
}

export { Languages, languageIds }

export const getLanguageByUrl = (): string | null => {
  if (isNotBrowser()) {
    return null
  }

  const path = window.location.pathname
  const pathSegments = path.split("/")
  if (pathSegments.length > 1 && languageIds.includes(pathSegments[1])) {
    return pathSegments[1]
  }
  return null
}

export const navigateWithLang = (toPath: string): void => {
  if (isNotBrowser()) {
    return
  }

  if ("/" != toPath.charAt(0) || toPath.indexOf("..") != -1) {
    throw new Error(
      "Only absolute paths are supported when using navigateWithLang"
    )
  }

  const langCode = getLanguageByUrl()
  if (langCode !== null) {
    navigate(`/${langCode}${toPath}`)
  } else {
    navigate(toPath)
  }
}

export const switchUrlLang = (targetLangCode: string): void => {
  const urlLangCode = getLanguageByUrl()
  if (urlLangCode === null) {
    return
  }

  if (!languageIds.includes(targetLangCode)) {
    return
  }

  const currentUrl = window.location.pathname + window.location.search
  const targetUrl = currentUrl.replace(urlLangCode, targetLangCode)
  navigate(targetUrl)
}
